/* Keyboard focus for the menus that open at a point: a square's, a name's, a
 * booking's.
 *
 * Opened from the keyboard, a menu has to take focus, or the desk has opened
 * something it cannot reach: Tab from the square goes on to the next square,
 * and the menu sits on screen out of the tab order. So focus moves to the
 * menu's first item, the arrows walk its items, and when it closes focus goes
 * back to whatever opened it.
 *
 * Only from the keyboard. Opened with a click or a tap, the menu leaves focus
 * where it was, as it always has: a focus ring appearing on a menu somebody
 * clicked open is noise, and on a touchscreen a focused time field would put
 * the keyboard up.
 */
import * as Vue from 'vue';

var ITEMS = '.ctx__item:not([disabled])';
var FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="0"]';

function openedFromKeyboard(el) {
  try { return !!el && el !== document.body && el.matches(':focus-visible'); }
  catch (e) { return false; }
}

export function useMenuFocus(isOpen) {
  var menuEl = Vue.ref(null);
  var opener = null;

  Vue.watch(isOpen, function (open) {
    if (open) {
      var from = document.activeElement;
      opener = openedFromKeyboard(from) ? from : null;
      if (!opener) return;
      Vue.nextTick(function () {
        var el = menuEl.value;
        if (!el) return;
        var first = el.querySelector(ITEMS) || el.querySelector(FOCUSABLE);
        if (first) first.focus({ preventScroll: true });
      });
      return;
    }
    var back = opener;
    opener = null;
    if (back && document.contains(back)) back.focus({ preventScroll: true });
  });

  // Up and Down between the menu's items; Home and End to either end.
  function onMenuKey(evt) {
    var el = menuEl.value;
    if (!el) return;
    var keys = { ArrowDown: 1, ArrowUp: -1, Home: 'first', End: 'last' };
    var step = keys[evt.key];
    if (step == null) return;
    var tag = evt.target && evt.target.tagName;
    if (tag === 'INPUT' || tag === 'SELECT') return;   // a time field steps its own value
    var items = Array.prototype.slice.call(el.querySelectorAll(ITEMS));
    if (!items.length) return;
    evt.preventDefault();
    var i = items.indexOf(document.activeElement);
    var to = step === 'first' ? 0
      : step === 'last' ? items.length - 1
      : i === -1 ? (step > 0 ? 0 : items.length - 1)
      : (i + step + items.length) % items.length;
    items[to].focus();
  }

  return { menuEl: menuEl, onMenuKey: onMenuKey };
}
